import { IApiClient, getApiClient } from "./ApiClient/ApiClient";
import logger, { LoggingSource } from "@/shared/Logger";
import IResponseModel from "./ResponseModel";
import DonorPayment, {
  DonorPaymentSummary,
} from "@/models/APIModels/DonorPayment";
import ChargeDetail from "@/models/APIModels/ChargeDetail";
import {
  IncomingPaymentTransactionDetail,
  IncomingPaymentTransactionSummary,
} from "@/models/APIModels/IncomingPaymentTransactionList";

export default class DonorPaymentApi {
  private readonly _apiClient: IApiClient;
  private readonly URL: string = "/donorpayment";

  constructor(apiClient: IApiClient = getApiClient()) {
    this._apiClient = apiClient;
  }

  async getList(queryString: string): Promise<IResponseModel<DonorPayment[]>> {
    try {
      logger.info("getDonorPaymentList...", LoggingSource.UIDonorPayment);

      logger.timeStart(
        "getDonorPaymentList call",
        LoggingSource.UIDonorPayment,
      );

      const result = await this._apiClient.get<DonorPayment[]>(
        `${this.URL}?${queryString}`,
      );

      logger.timeEnd("getDonorPaymentList call", LoggingSource.UIDonorPayment);

      logger.debug(
        `getDonorPaymentList response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonorPayment,
      );
      return result;
    } catch (error) {
      logger.timeEnd("getDonorPaymentList call", LoggingSource.UIDonorPayment);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorPayment);
      throw error;
    } finally {
      logger.info("...getDonorPaymentList", LoggingSource.UIDonorPayment);
    }
  }

  async getTransactionsList(
    queryString: string,
  ): Promise<IResponseModel<IncomingPaymentTransactionDetail[]>> {
    try {
      logger.info("getTransactionsList...", LoggingSource.UIDonorPayment);

      logger.timeStart(
        "getTransactionsList call",
        LoggingSource.UIDonorPayment,
      );

      const result = await this._apiClient.get<
        IncomingPaymentTransactionDetail[]
      >(`${this.URL}/transactions/list?${queryString}`);

      logger.timeEnd("getTransactionsList call", LoggingSource.UIDonorPayment);

      logger.debug(
        `getTransactionsList response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonorPayment,
      );
      return result;
    } catch (error) {
      logger.timeEnd("getTransactionsList call", LoggingSource.UIDonorPayment);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorPayment);
      throw error;
    } finally {
      logger.info("...getTransactionsList", LoggingSource.UIDonorPayment);
    }
  }

  async chargeAccount(
    userReference: string,
  ): Promise<IResponseModel<ChargeDetail>> {
    try {
      logger.info("chargeAccount...", LoggingSource.UIDonorPayment);

      logger.timeStart("chargeAccount call", LoggingSource.UIDonorPayment);

      const result = await this._apiClient.get<ChargeDetail>(
        `${this.URL}/${userReference}/chargeaccount`,
      );

      logger.timeEnd("chargeAccount call", LoggingSource.UIDonorPayment);

      logger.debug(
        `chargeAccount response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonorPayment,
      );
      return result;
    } catch (error) {
      logger.timeEnd("chargeAccount call", LoggingSource.UIDonorPayment);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorPayment);
      throw error;
    } finally {
      logger.info("...chargeAccount", LoggingSource.UIDonorPayment);
    }
  }

  async refundAccount(
    userReference: string,
    queryString: string,
  ): Promise<IResponseModel<ChargeDetail>> {
    try {
      logger.info("refundAccount...", LoggingSource.UIDonorPayment);

      logger.timeStart("refundAccount call", LoggingSource.UIDonorPayment);

      logger.debug(
        `Parameter => UserReference: ${userReference}, QueryString: ${queryString}`,
        LoggingSource.UIDonorPayment,
      );

      const result = await this._apiClient.get<ChargeDetail>(
        `${this.URL}/${userReference}/refundaccount?${queryString}`,
      );

      logger.timeEnd("refundAccount call", LoggingSource.UIDonorPayment);

      logger.debug(
        `refundAccount response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonorPayment,
      );
      return result;
    } catch (error) {
      logger.timeEnd("refundAccount call", LoggingSource.UIDonorPayment);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorPayment);
      throw error;
    } finally {
      logger.info("...refundAccount", LoggingSource.UIDonorPayment);
    }
  }

  async donorPaymentSummary(
    queryString: string,
  ): Promise<IResponseModel<DonorPaymentSummary>> {
    try {
      logger.info("donorPaymentSummary...", LoggingSource.UIDonorPayment);
      logger.timeStart(
        "donorPaymentSummary call",
        LoggingSource.UIDonorPayment,
      );

      const result = await this._apiClient.get<string>(
        `${this.URL}/summary?${queryString}`,
      );
      logger.timeEnd("donorPaymentSummary call", LoggingSource.UIDonorPayment);

      logger.debug(
        `donorPaymentSummary response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonorPayment,
      );
      let paymentSummary: IResponseModel<DonorPaymentSummary> = {
        ...result,
      } as IResponseModel<DonorPaymentSummary>;

      if (result.content) paymentSummary.content = JSON.parse(result.content);
      return paymentSummary;
    } catch (error) {
      logger.timeEnd("donorPaymentSummary call", LoggingSource.UIDonorPayment);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorPayment);
      throw error;
    } finally {
      logger.info("...donorPaymentSummary", LoggingSource.UIDonorPayment);
    }
  }

  async getTransactionsSummary(): Promise<
    IResponseModel<IncomingPaymentTransactionSummary>
  > {
    try {
      logger.info("getTransactionsSummary...", LoggingSource.UIDonorPayment);
      logger.timeStart(
        "getTransactionsSummary call",
        LoggingSource.UIDonorPayment,
      );

      const result =
        await this._apiClient.get<IncomingPaymentTransactionSummary>(
          `${this.URL}/transactions/summary`,
        );
      logger.timeEnd(
        "getTransactionsSummary call",
        LoggingSource.UIDonorPayment,
      );

      logger.debug(
        `getTransactionsSummary response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonorPayment,
      );

      return result;
    } catch (error) {
      logger.timeEnd(
        "getTransactionsSummary call",
        LoggingSource.UIDonorPayment,
      );
      logger.error(JSON.stringify(error), LoggingSource.UIDonorPayment);
      throw error;
    } finally {
      logger.info("...getTransactionsSummary", LoggingSource.UIDonorPayment);
    }
  }
}
